import { PackageCard } from "@/components/quote/PackageCard";
import { Camera } from "lucide-react";
import { packageIncludesPhoto } from "@/lib/quoteContent";

function packagesGridClass(count) {
  if (count >= 4) return "md:grid-cols-2 lg:grid-cols-4";
  if (count === 3) return "md:grid-cols-3";
  if (count === 2) return "md:grid-cols-2 max-w-3xl mx-auto";
  return "md:grid-cols-1 max-w-md mx-auto";
}

function packageIncludesGuestbook(pkg) {
  const label = `${pkg.name} ${pkg.subtitle || ""}`;
  return /video/i.test(label) && !/highlight/i.test(label);
}

export function InvestmentSection({ quote, selectedPackages, onSelect }) {
  const deposit =
    quote.deposit_type && quote.deposit_value
      ? { type: quote.deposit_type, value: quote.deposit_value }
      : null;
  const photoPackages = selectedPackages.filter(packageIncludesPhoto);
  const showPreweddingBanner = quote.prewedding_shoot_offer && photoPackages.length > 0;
  const preweddingMixed = showPreweddingBanner && photoPackages.length < selectedPackages.length;
  return (
    <section
      data-testid="investment-section"
      data-track-section="packages"
      className="bg-[#F3F1ED] px-6 md:px-12 lg:px-16 py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16 md:mb-20">
          <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-none text-[#1F1E1D] mb-6">
            Your Investment
          </h2>
          <p className="text-[#5C5A56] max-w-xl mx-auto text-lg">
            You're investing so much into making your day perfect. This is the part that means it lasts. Long after everything else is over, these memories are yours for a lifetime.
          </p>
        </div>

        {showPreweddingBanner && (
          <div
            data-testid="prewedding-shoot-banner"
            className="max-w-3xl mx-auto mb-14 md:mb-16 bg-white border border-[#CCBA56]/60 shadow-[0_10px_40px_rgba(0,0,0,0.06)] p-8 md:p-10 text-center"
          >
            <div className="inline-flex items-center gap-2 text-[10px] tracking-overline uppercase font-medium bg-[#CCBA56] text-[#1F1E1D] px-4 py-1.5 rounded-full mb-5">
              <Camera className="w-3.5 h-3.5" strokeWidth={2} />
              Included free with your quote
            </div>
            <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[#1F1E1D] mb-4">
              Pre-Wedding Couple Photoshoot
            </h3>
            <p className="text-[#5C5A56] leading-relaxed max-w-xl mx-auto mb-4">
              At our studio in Wickford, your wedding venue or a beautiful
              country park in Essex. Feel comfortable in front of the camera
              with a 60 minute pre-wedding photoshoot.
            </p>
            <p className="text-sm">
              <span className="text-[#8A8782] line-through tabular-nums">
                Usually £{Number(quote.prewedding_shoot_offer_price || 249).toLocaleString("en-GB")}
              </span>{" "}
              <span className="text-[#B5A244] font-semibold">Included free</span>
            </p>
            {preweddingMixed && (
              <p data-testid="prewedding-photo-only-note" className="text-xs text-[#8A8782] mt-3">
                Included with collections that feature photography.
              </p>
            )}
          </div>
        )}

        <div className={`grid gap-6 md:gap-8 items-start ${packagesGridClass(selectedPackages.length)}`}>
          {selectedPackages.map((p) => (
            <PackageCard
              key={p.id}
              pkg={p}
              onSelect={onSelect}
              deposit={deposit}
              showGuestbook={quote.video_booth_offer && packageIncludesGuestbook(p)}
              showPrewedding={!!quote.prewedding_shoot_offer && packageIncludesPhoto(p)}
              preweddingPrice={quote.prewedding_shoot_offer_price || 249}
            />
          ))}
        </div>

        {quote.content_creator_offer && (
          <div
            data-testid="content-creator-showcase"
            className="max-w-3xl mx-auto mt-14 md:mt-16 bg-[#21201F] text-white shadow-[0_20px_60px_rgba(0,0,0,0.18)] p-8 md:p-10"
          >
            <div className="inline-flex items-center gap-2 text-[10px] tracking-overline uppercase font-medium bg-[#CCBA56] text-[#1F1E1D] px-4 py-1.5 rounded-full mb-5">
              <Camera className="w-3.5 h-3.5" strokeWidth={2} />
              Optional add-on. Book with any collection
            </div>
            <h3 className="font-display text-3xl md:text-4xl tracking-tight mb-2">
              Content Creation
            </h3>
            <p className="mb-6">
              {Number(quote.content_creator_offer_price || 695) < 995 && (
                <span className="text-white/40 line-through tabular-nums mr-2">£995</span>
              )}
              <span className="text-[#CCBA56] font-semibold text-xl tabular-nums">
                +£{Number(quote.content_creator_offer_price || 695).toLocaleString("en-GB")}
              </span>
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 text-sm text-white/85 leading-relaxed mb-6">
              {[
                "Your own dedicated wedding content creator",
                "Up to 8 hours of coverage on your day",
                "Filmed vertically, ready for Instagram, TikTok & Facebook",
                "Sneak peek reel delivered within 24 hours",
                "3 fully edited social-ready reels",
                "Every single raw clip transferred to you",
                "Behind-the-scenes moments the cameras usually miss",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <span className="text-[#CCBA56] mt-0.5">&#10003;</span>
                  {f}
                </li>
              ))}
            </ul>
            <p className="text-xs text-white/50">
              Simply tick the Content Creator box when you secure your date with
              any of the collections above.
            </p>
          </div>
        )}
        {deposit && (
          <p
            data-testid="deposit-note"
            className="text-center text-sm text-[#5C5A56] mt-12 max-w-xl mx-auto leading-relaxed"
          >
            Pay just the deposit to secure your date. The remaining balance can
            be spread across <strong>interest-free monthly payments</strong> once
            your date is locked in.
          </p>
        )}
      </div>
    </section>
  );
}
